import { sharedIntegrationsAvailable } from '@mygpt/app/config/integrations';
import {
  Box,
  Button,
  Collapse,
  Group,
  Highlight,
  NumberInput,
  Select,
  Slider,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { UseFormReturnType } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBrain,
  IconCalculator,
  IconLicense,
  IconMessageCircle,
  IconMessageDots,
  IconPlugConnected,
  IconRobot,
  IconSearch,
} from '@tabler/icons-react';
import cx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BotQuota, RobotRequestFormConfigs, useDateLocal, UpdateRobotParams } from '@mygpt/app';
import { BetaBadge } from '@/components/Common/BetaBadge';
import { InfoTooltip } from '@/components/Common/InfoTooltip';
import MessageUsageModal from '@/components/Settings/MessageUsageModal';
import { SvgArrow } from '@/components/Widget/icon';
import FeedbackContentEditor from '@/components/Editor/FeedbackContentEditor';
import { isNewAgentType } from '@mygpt/app/utils/robotType';
import { MemoryPreview } from './MemoryPreview';
import { SettingsGroup } from './SettingsGroup';
import { SettingRow } from './SettingRow';

const yesterday = new Date();
yesterday.setDate(yesterday.getDate() - 1);

function getTemperatureDescription(value: number, t: (key: string) => string): string {
  if (value === 0) return t('settings.style.chat_temperature_level_0');
  if (value <= 30) return t('settings.style.chat_temperature_level_low');
  if (value <= 60) return t('settings.style.chat_temperature_level_mid');
  if (value <= 80) return t('settings.style.chat_temperature_level_high');
  return t('settings.style.chat_temperature_level_max');
}

interface Props {
  datasetId: string;
  documentForm: UseFormReturnType<RobotRequestFormConfigs>;
  onOverrideChangeForConfigForm: (prop: string) => (data?: any) => void;
  robotPlanInfo: BotQuota | undefined;
  submitInNextTick: () => void;
  robotInfo: UpdateRobotParams | undefined;
}

// Anchor navigation group IDs
const GROUP_IDS = {
  aiCore: 'settings-ai-core',
  agent: 'settings-agent',
  search: 'settings-search',
  response: 'settings-response',
  limit: 'settings-limit',
  addon: 'settings-addon',
} as const;

export const Advanced = ({
  datasetId,
  documentForm,
  onOverrideChangeForConfigForm,
  robotPlanInfo,
  submitInNextTick,
  robotInfo,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { dateLocale } = useDateLocal();
  const feedbackContentEditorRef = useRef<any>(null);
  const isLocalMode = import.meta.env.VITE_MODE === 'local';

  const [usageOpened, { close: closeUsage, open: openUsage }] = useDisclosure(false);
  const [searchEngineOpened, { toggle: toggleSearchEngine }] = useDisclosure(false);
  const [activeAnchor, setActiveAnchor] = useState(GROUP_IDS.aiCore);
  // 各分组的展开信号：点击锚点导航时递增对应组的值，触发折叠卡片自动展开
  const [openTick, setOpenTick] = useState<Record<string, number>>({});

  const canUsedMessage = useMemo(() => {
    if (!robotPlanInfo) return 0;
    return robotPlanInfo.effective_quotas.message_quota.available;
  }, [robotPlanInfo]);

  const TALKING_STYLE = useMemo(() => {
    return [
      {
        label: t('settings.style.options.default'),
        value: 'default',
      },
      {
        label: t('settings.style.options.genial'),
        value: 'genial',
      },
      {
        label: t('settings.style.options.humor'),
        value: 'humor',
      },
      {
        label: t('settings.style.options.lovely'),
        value: 'lovely',
      },
      {
        label: t('settings.style.options.rigorous'),
        value: 'rigorous',
      },
    ];
  }, [t]);

  const onFeedbackContentSave = () => {
    const content = feedbackContentEditorRef.current.getHTML();
    documentForm.setFieldValue('feedback_form.content', content);
    submitInNextTick();
  };

  // 计算各分组的项目数
  const isAgent = isNewAgentType(robotInfo?.robot_type);

  const aiCoreCount = useMemo(() => {
    let count = 1; // fallback_to_chatgpt
    if (!isLocalMode) count++; // talking_style
    if (!isLocalMode) count++; // chat_temperature
    return count;
  }, [isLocalMode]);

  const agentCount = useMemo(() => {
    if (!isAgent) return 0;
    return 2; // enable_thinking, enable_memory
  }, [isAgent]);

  const searchCount = useMemo(() => {
    let count = 2; // recommend, faq_search_answer_with_embedding
    if (!isLocalMode) count++; // enable_web_search
    return count;
  }, [isLocalMode]);

  const responseCount = useMemo(() => {
    let count = 1; // unknown_text
    count++; // enable_images
    if (isLocalMode) count++; // enable_llm_metrics（仅 onprem 环境显示）
    if (!isLocalMode) count++; // feedback
    count++; // qa_recommendation
    if (!isLocalMode && sharedIntegrationsAvailable) count++; // enable_tts
    return count;
  }, [isLocalMode]);

  const limitCount = useMemo(() => {
    if (isLocalMode) return 0;
    const count = 1; // quota
    return count;
  }, [isLocalMode]);

  const addonCount = useMemo(() => {
    let count = 1; // disable_third_party_url_access
    if (!isLocalMode) count++; // notification_admin（从用量限制移入）
    return count;
  }, [isLocalMode]);

  // 点击锚点后锁定 scroll-spy：底部的「利用制限」「付加機能」是短分组，
  // IntersectionObserver 的检测区(rootMargin 底部 -60%)永远覆盖不到它们，
  // 导致点击后高亮会被中间分组抢占。锁定后直到用户真正滚动(wheel/touch)才恢复自动检测。
  const isManualScrollingRef = useRef(false);

  // 用户主动滚动时释放锁，恢复 scroll-spy 自动高亮
  useEffect(() => {
    const releaseManualLock = () => {
      isManualScrollingRef.current = false;
    };
    window.addEventListener('wheel', releaseManualLock, { passive: true });
    window.addEventListener('touchmove', releaseManualLock, { passive: true });
    return () => {
      window.removeEventListener('wheel', releaseManualLock);
      window.removeEventListener('touchmove', releaseManualLock);
    };
  }, []);

  // Anchor navigation - scroll spy using IntersectionObserver
  useEffect(() => {
    const ids = [
      GROUP_IDS.aiCore,
      ...(isAgent ? [GROUP_IDS.agent] : []),
      GROUP_IDS.search,
      GROUP_IDS.response,
      ...(!isLocalMode ? [GROUP_IDS.limit] : []),
      GROUP_IDS.addon,
    ];

    const observer = new IntersectionObserver(
      (entries) => {
        // 手动点击锚点后暂停自动高亮，避免平滑滚动经过的中间分组抢占选中态
        if (isManualScrollingRef.current) return;

        // Find the topmost visible section
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        if (visible.length > 0) {
          setActiveAnchor(visible[0].target.id);
        }
      },
      { rootMargin: '-100px 0px -60% 0px', threshold: 0 }
    );

    ids.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [isAgent, isLocalMode]);

  const scrollToGroup = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;

    // 立即更新高亮，不再依赖 observer 检测（底部短分组永远进不了检测区）
    setActiveAnchor(id);
    // 锁定 scroll-spy，直到用户下一次主动滚动
    isManualScrollingRef.current = true;
    // 若目标卡片处于折叠状态则自动展开
    setOpenTick((prev) => ({ ...prev, [id]: (prev[id] ?? 0) + 1 }));

    el.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // 边框闪烁提示当前选中的分组。闪烁需加在内层卡片上：
    // 外层 div 的内阴影会被卡片的不透明白底遮住，看不到效果。
    // 移除+强制重排以支持重复点击时重启动画。
    const card = (el.firstElementChild as HTMLElement) || el;
    card.classList.remove('settings-anchor-flash');
    void card.offsetWidth;
    card.classList.add('settings-anchor-flash');
  };

  // Build anchor navigation items
  const anchorItems = useMemo(() => {
    const items = [
      { id: GROUP_IDS.aiCore, label: t('settings.group.ai_core') },
      ...(isAgent ? [{ id: GROUP_IDS.agent, label: t('settings.group.agent') }] : []),
      { id: GROUP_IDS.search, label: t('settings.group.search') },
      { id: GROUP_IDS.response, label: t('settings.group.response') },
      ...(!isLocalMode ? [{ id: GROUP_IDS.limit, label: t('settings.group.limit') }] : []),
      { id: GROUP_IDS.addon, label: t('settings.group.addon') },
    ];
    return items;
  }, [isAgent, isLocalMode, t]);

  return (
    <>
      <div className="flex gap-6">
        {/* Main content */}
        <div className="flex-1 space-y-4 min-w-0">
          {/* Group 1: AI Core Settings */}
          <div id={GROUP_IDS.aiCore}>
            <SettingsGroup
              icon={<IconBrain size={18} />}
              iconBg="#F3E8FF"
              iconColor="#7C3AED"
              title={t('settings.group.ai_core')}
              count={aiCoreCount}
              openSignal={openTick[GROUP_IDS.aiCore]}
            >
              <SettingRow>
                <Group justify="space-between" align="center">
                  <div>
                    <Text size="sm">{t('create.form.label.usegpt_label')}</Text>
                    <Text size="xs" c="dimmed">
                      {t('create.form.label.usegpt_desc')}
                    </Text>
                  </div>
                  <Switch
                    checked={documentForm.values.fallback_to_chatgpt}
                    {...documentForm.getInputProps('fallback_to_chatgpt')}
                    onChange={onOverrideChangeForConfigForm('fallback_to_chatgpt')}
                  />
                </Group>
              </SettingRow>
              {!isLocalMode && (
                <SettingRow>
                  <Select
                    label={
                      <Group gap={5} mb={5}>
                        <Text size="sm" fw={400}>
                          {t('settings.style.talking_style')}
                        </Text>
                        <InfoTooltip label={t('settings.style.talking_style_desc')} position="right" />
                      </Group>
                    }
                    data={TALKING_STYLE}
                    {...documentForm.getInputProps('talking_style')}
                    onChange={onOverrideChangeForConfigForm('talking_style')}
                    placeholder={t('settings.style.talking_style')}
                    allowDeselect={false}
                  />
                </SettingRow>
              )}
              {!isLocalMode && (
                <SettingRow noBorder py={12}>
                  <Group gap={5}>
                    <Text size="sm">{t('settings.style.chat_temperature')}</Text>
                    <InfoTooltip label={t('settings.style.chat_temperature_desc')} position="right" />
                  </Group>
                  <Box className="flex-1 bg-gray-50 rounded-lg p-4 mt-2 mb-2">
                    <Group justify="space-between" mb={8}>
                      <Text size="xs" c="dimmed">
                        {t('settings.style.chat_temperature_low')}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {t('settings.style.chat_temperature_high')}
                      </Text>
                    </Group>
                    <Slider
                      label={null}
                      step={10}
                      marks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((number) => ({
                        value: number,
                      }))}
                      {...documentForm.getInputProps('chat_temperature')}
                      onChange={onOverrideChangeForConfigForm('chat_temperature')}
                    />
                    <Text size="xs" c="dimmed" ta="center" mt={12}>
                      {t('settings.style.chat_temperature_current', {
                        value: documentForm.values.chat_temperature ?? 0,
                        desc: getTemperatureDescription(documentForm.values.chat_temperature ?? 0, t),
                      })}
                    </Text>
                  </Box>
                </SettingRow>
              )}
            </SettingsGroup>
          </div>

          {/* Group 2: Agent Settings */}
          {isAgent && (
            <div id={GROUP_IDS.agent}>
              <SettingsGroup
                icon={<IconRobot size={18} />}
                iconBg="#DBEAFE"
                iconColor="#2563EB"
                title={t('settings.group.agent')}
                count={agentCount}
                openSignal={openTick[GROUP_IDS.agent]}
              >
                <SettingRow>
                  <Group justify="space-between" align="center">
                    <Text size="sm">{t('settings.agent.enable_thinking')}</Text>
                    <Switch
                      checked={documentForm.values.enable_thinking}
                      {...documentForm.getInputProps('enable_thinking')}
                      onChange={onOverrideChangeForConfigForm('enable_thinking')}
                    />
                  </Group>
                </SettingRow>
                <SettingRow noBorder>
                  <Group justify="space-between" align="center">
                    <Text size="sm">{t('settings.agent.enable_memory')}</Text>
                    <Switch
                      checked={documentForm.values.enable_memory}
                      {...documentForm.getInputProps('enable_memory')}
                      onChange={onOverrideChangeForConfigForm('enable_memory')}
                    />
                  </Group>
                  <MemoryPreview botId={robotInfo?.aiId || ''} enabled={documentForm.values.enable_memory} />
                </SettingRow>
              </SettingsGroup>
            </div>
          )}

          {/* Group 3: Search & Knowledge */}
          <div id={GROUP_IDS.search}>
            <SettingsGroup
              icon={<IconSearch size={18} />}
              iconBg="#D1FAE5"
              iconColor="#059669"
              title={t('settings.group.search')}
              count={searchCount}
              openSignal={openTick[GROUP_IDS.search]}
            >
              <SettingRow>
                <Group justify="space-between" align="center">
                  <Group gap={5}>
                    <Text size="sm">{t('settings.faq.question_title')}</Text>
                    <InfoTooltip label={t('settings.faq.question_desc')} position="right" />
                  </Group>
                  <Switch
                    checked={documentForm.values.recommend}
                    {...documentForm.getInputProps('recommend')}
                    onChange={onOverrideChangeForConfigForm('recommend')}
                  />
                </Group>
              </SettingRow>
              <SettingRow>
                <Group justify="space-between" align="center">
                  <Group gap={5}>
                    <Text size="sm">{t('settings.faq.answer_title')}</Text>
                    <InfoTooltip
                      position="right"
                      label={
                        <Highlight
                          component="span"
                          className="text-sm"
                          highlight={t('settings.faq.answer_desc_height')}
                          onClick={() => {
                            navigate(`/datasets/${datasetId}/data-source/faq`);
                          }}
                        >
                          {t('settings.faq.answer_desc')}
                        </Highlight>
                      }
                    />
                  </Group>
                  <Switch
                    checked={documentForm.values.faq_search_answer_with_embedding}
                    {...documentForm.getInputProps('faq_search_answer_with_embedding')}
                    onChange={onOverrideChangeForConfigForm('faq_search_answer_with_embedding')}
                  />
                </Group>
              </SettingRow>
              {!isLocalMode && (
                <SettingRow noBorder>
                  <Group justify="space-between" align="flex-start">
                    <div>
                      <Group gap={5}>
                        <Text size="sm">{t('settings.search_engine.label')}</Text>
                        <BetaBadge />
                      </Group>
                      <Text size="xs" c="dimmed">
                        {t('settings.search_engine.desc')}
                        <Text size="xs" c="red" span inherit ml={5}>
                          {t('settings.chat_images.warn_tip')}
                        </Text>
                      </Text>
                    </div>
                    <Switch
                      checked={documentForm.values.enable_web_search}
                      {...documentForm.getInputProps('enable_web_search')}
                      onChange={onOverrideChangeForConfigForm('enable_web_search')}
                    />
                  </Group>
                  <Box className="flex-1 bg-gray-50 rounded-lg p-5 mt-2">
                    <Stack w="100%">
                      <div className="flex items-center w-full">
                        <Text className="mr-2">site:</Text>
                        <TextInput
                          disabled={!documentForm.values.enable_web_search}
                          placeholder={t('settings.search_engine.web_search_site_list')}
                          w={'100%'}
                          {...documentForm.getInputProps('web_search_site_list')}
                          onBlur={onOverrideChangeForConfigForm('web_search_site_list')}
                        />
                      </div>
                      <Group onClick={toggleSearchEngine} gap={0} className="cursor-pointer">
                        <Text size={'sm'}>{t('settings.search_engine.web_search_site_list_sample_label')}</Text>
                        <span className={cx(searchEngineOpened && 'rotate-180', 'transition-all')}>
                          <SvgArrow />
                        </span>
                      </Group>
                      <Collapse in={searchEngineOpened}>
                        <Table striped highlightOnHover withTableBorder withColumnBorders>
                          <Table.Thead>
                            <Table.Tr>
                              <Table.Th>{t('settings.search_engine.web_search_site_list_sample_value_label')}</Table.Th>
                              <Table.Th>{t('settings.search_engine.web_search_site_list_sample_desc_label')}</Table.Th>
                            </Table.Tr>
                          </Table.Thead>
                          <Table.Tbody>
                            <Table.Tr>
                              <Table.Td>example.com</Table.Td>
                              <Table.Td>{t('settings.search_engine.web_search_site_list_sample_desc_desc_1')}</Table.Td>
                            </Table.Tr>
                            <Table.Tr>
                              <Table.Td>https://www.example.com/ramen</Table.Td>
                              <Table.Td>{t('settings.search_engine.web_search_site_list_sample_desc_desc_2')}</Table.Td>
                            </Table.Tr>
                          </Table.Tbody>
                        </Table>
                      </Collapse>
                    </Stack>
                  </Box>
                </SettingRow>
              )}
            </SettingsGroup>
          </div>

          {/* Group 4: Response Customization */}
          <div id={GROUP_IDS.response}>
            <SettingsGroup
              icon={<IconMessageDots size={18} />}
              iconBg="#FEF3C7"
              iconColor="#D97706"
              title={t('settings.group.response')}
              count={responseCount}
              openSignal={openTick[GROUP_IDS.response]}
            >
              <SettingRow>
                <Stack gap={5}>
                  <Text size="sm">{t('settings.style.unknown_text')}</Text>
                  <Text size="xs" c="gray.5">
                    {t('settings.style.unknown_text_desc')}
                  </Text>
                  <Textarea
                    autosize
                    minRows={2}
                    maxRows={6}
                    placeholder={t('settings.style.unknown_text_placeholder')}
                    w={'100%'}
                    {...documentForm.getInputProps('unknown_text')}
                    onBlur={onOverrideChangeForConfigForm('unknown_text')}
                  />
                </Stack>
              </SettingRow>
              <SettingRow>
                <Group justify="space-between" align="center">
                  <div>
                    <Group gap={5}>
                      <Text size="sm">{t('settings.chat_images.title')}</Text>
                      <BetaBadge />
                    </Group>
                    <Text size="xs" c="dimmed">
                      {t('settings.chat_images.label')}
                      <Text size="xs" c="red" span inherit ml={5}>
                        {t('settings.chat_images.warn_tip')}
                      </Text>
                    </Text>
                  </div>
                  <Switch
                    checked={documentForm.values.enable_images}
                    {...documentForm.getInputProps('enable_images')}
                    onChange={onOverrideChangeForConfigForm('enable_images')}
                  />
                </Group>
              </SettingRow>
              {/* enable_llm_metrics 是否显示LLM服务的指标信息，仅 onprem 环境下显示 */}
              {isLocalMode && (
                <SettingRow>
                  <Group justify="space-between" align="center">
                    <div>
                      <Text size="sm">{t('settings.llm_metrics.label')}</Text>
                      <Text size="xs" c="dimmed">
                        {t('settings.llm_metrics.desc')}
                      </Text>
                    </div>
                    <Switch
                      checked={documentForm.values.enable_llm_metrics}
                      {...documentForm.getInputProps('enable_llm_metrics')}
                      onChange={onOverrideChangeForConfigForm('enable_llm_metrics')}
                    />
                  </Group>
                </SettingRow>
              )}
              <SettingRow>
                <Group justify="space-between" align="center">
                  <div>
                    <Text size="sm">{t('settings.qa-recommendation.label')}</Text>
                    <Text size="xs" c="dimmed">
                      {t('settings.qa-recommendation.desc')}
                    </Text>
                  </div>
                  <Switch
                    checked={documentForm.values.qa_recommendation}
                    {...documentForm.getInputProps('qa_recommendation')}
                    onChange={onOverrideChangeForConfigForm('qa_recommendation')}
                  />
                </Group>
              </SettingRow>
              {!isLocalMode && sharedIntegrationsAvailable && (
                <SettingRow>
                  <Group justify="space-between" align="center">
                    <div>
                      <Text size="sm">{t('settings.tts.label')}</Text>
                      <Text size="xs" c="dimmed">
                        {t('settings.tts.desc')}
                      </Text>
                    </div>
                    <Switch
                      checked={documentForm.values.enable_tts}
                      {...documentForm.getInputProps('enable_tts')}
                      onChange={onOverrideChangeForConfigForm('enable_tts')}
                    />
                  </Group>
                </SettingRow>
              )}
              {!isLocalMode && (
                <SettingRow noBorder>
                  <Stack>
                    <Text size="sm">{t('settings.feedback.name')}</Text>
                    <Text size="xs" c="gray.5">
                      {t('settings.feedback.desc')}
                    </Text>
                    <Stack className="bg-gray-50 rounded-lg p-5">
                      <div className="mt-1">
                        <FeedbackContentEditor
                          ref={feedbackContentEditorRef}
                          value={documentForm.values.feedback_form.content}
                        />
                      </div>
                      <Button onClick={onFeedbackContentSave}>{t('save')}</Button>
                      <Switch
                        label={t('settings.feedback.forward_chat_history')}
                        description={t('settings.feedback.forward_chat_history_desc')}
                        checked={documentForm.values.feedback_form.forward_chat_history}
                        onChange={(e) => {
                          documentForm.setFieldValue('feedback_form.forward_chat_history', e.currentTarget.checked);
                          submitInNextTick();
                        }}
                      />
                      {documentForm.values.feedback_form.forward_chat_history && (
                        <>
                          <TextInput
                            label={t('settings.feedback.visible_text')}
                            description={t('settings.feedback.visible_text_desc')}
                            value={documentForm.values.feedback_form.forward_chat_history_visible_text || ''}
                            onChange={(e) =>
                              documentForm.setFieldValue(
                                'feedback_form.forward_chat_history_visible_text',
                                e.currentTarget.value
                              )
                            }
                            onBlur={() => submitInNextTick()}
                          />
                          <Textarea
                            label={t('settings.feedback.ai_instruction')}
                            description={t('settings.feedback.ai_instruction_desc')}
                            value={documentForm.values.feedback_form.forward_chat_history_ai_instruction || ''}
                            onChange={(e) =>
                              documentForm.setFieldValue(
                                'feedback_form.forward_chat_history_ai_instruction',
                                e.currentTarget.value
                              )
                            }
                            onBlur={() => submitInNextTick()}
                            autosize
                            minRows={2}
                          />
                        </>
                      )}
                    </Stack>
                  </Stack>
                </SettingRow>
              )}
            </SettingsGroup>
          </div>

          {/* Group 5: Usage Limits */}
          {!isLocalMode && (
            <div id={GROUP_IDS.limit}>
              <SettingsGroup
                icon={<IconCalculator size={18} />}
                iconBg="#FCE7F3"
                iconColor="#DB2777"
                title={t('settings.group.limit')}
                count={limitCount}
                openSignal={openTick[GROUP_IDS.limit]}
              >
                <SettingRow noBorder>
                  <Box className="flex-1 bg-gray-50 rounded-lg p-5">
                    <Stack>
                      <Group justify="space-between" wrap="nowrap">
                        <Stack gap={5}>
                          <Group gap={'xs'}>
                            <IconLicense size={18} color="gray" />
                            <Text size="sm" c="gray.8">
                              {t('settings.quota.title')}
                            </Text>
                            <InfoTooltip label={t('settings.quota.desc')} position="right" />
                          </Group>
                          <Group gap={0}>
                            <Text size="xs" c="gray.5">
                              {t('settings.quota.current_usage')}
                            </Text>
                            <Text size="xs" c="violet">
                              {canUsedMessage !== null ? canUsedMessage : t('unlimited')}
                            </Text>
                          </Group>
                        </Stack>

                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => {
                            openUsage();
                          }}
                        >
                          {t('settings.quota.usage_logs')}
                        </Button>
                      </Group>

                      <Group grow>
                        <NumberInput
                          label={
                            <Text size="sm" mb={5}>
                              {t('settings.quota.set_quota_label')}
                            </Text>
                          }
                          autoComplete="off"
                          allowNegative={false}
                          placeholder={t('settings.quota.set_quota_placeholder')}
                          hideControls
                          leftSection={<IconMessageCircle size={18} />}
                          {...documentForm.getInputProps('max_questions')}
                          onBlur={onOverrideChangeForConfigForm('max_questions')}
                          allowLeadingZeros={true}
                        />
                        <DatePickerInput
                          type="range"
                          label={
                            <Text size="sm" mb={5}>
                              {t('settings.quota.custom_time_label')}
                            </Text>
                          }
                          locale={dateLocale}
                          size="sm"
                          numberOfColumns={2}
                          valueFormat="YYYY-MM-DD"
                          labelSeparator="~"
                          clearable
                          excludeDate={(date) => date < yesterday}
                          leftSection={<IconCalculator size={18} />}
                          allowSingleDateInRange
                          {...documentForm.getInputProps('question_date')}
                          onChange={onOverrideChangeForConfigForm('question_date')}
                        />
                      </Group>
                    </Stack>
                  </Box>
                </SettingRow>
              </SettingsGroup>
            </div>
          )}

          {/* Group 6: Additional Features */}
          <div id={GROUP_IDS.addon}>
            <SettingsGroup
              icon={<IconPlugConnected size={18} />}
              iconBg="#CFFAFE"
              iconColor="#0891B2"
              title={t('settings.group.addon')}
              count={addonCount}
              defaultOpened={false}
              openSignal={openTick[GROUP_IDS.addon]}
            >
              {!isLocalMode && (window as any).ENV?.GBASE_SUPPORT_FRONTEND_HIDE_VISITOR_NOTIFICATION !== 'true' && (
                <SettingRow>
                  <Group justify="space-between" align="center">
                    <div>
                      <Text size="sm">{t('settings.notification.label')}</Text>
                      <Text size="xs" c="dimmed">
                        {t('settings.notification.desc')}
                      </Text>
                    </div>
                    <Switch
                      checked={documentForm.values.notification_admin}
                      {...documentForm.getInputProps('notification')}
                      onChange={onOverrideChangeForConfigForm('notification_admin')}
                    />
                  </Group>
                </SettingRow>
              )}
              <SettingRow noBorder>
                <Group justify="space-between" align="center">
                  <div>
                    <Text size="sm">{t('settings.third_party_access.title')}</Text>
                    <Text size="xs" c="dimmed">
                      {t('settings.third_party_access.desc')}
                    </Text>
                  </div>
                  <Switch
                    checked={documentForm.values.disable_third_party_url_access}
                    {...documentForm.getInputProps('disable_third_party_url_access')}
                    onChange={onOverrideChangeForConfigForm('disable_third_party_url_access')}
                  />
                </Group>
              </SettingRow>
            </SettingsGroup>
          </div>

          {/* 底部留白：让底部较矮的分组（如「利用制限」「付加機能」）点击锚点后也能滚动到顶部 */}
          <div aria-hidden className="hidden lg:block" style={{ height: 'calc(100vh - 240px)' }} />
        </div>

        {/* Anchor navigation sidebar */}
        <Box className="sticky top-4 hidden lg:block" style={{ width: 160, flexShrink: 0, alignSelf: 'flex-start' }}>
          <Stack gap={2}>
            {anchorItems.map((item) => (
              <Text
                key={item.id}
                size="xs"
                className="cursor-pointer rounded px-2 py-1.5 transition-colors"
                c={activeAnchor === item.id ? 'violet.6' : 'gray.5'}
                fw={activeAnchor === item.id ? 600 : 400}
                style={{
                  backgroundColor: activeAnchor === item.id ? 'var(--mantine-color-violet-0)' : 'transparent',
                  borderLeft:
                    activeAnchor === item.id ? '2px solid var(--mantine-color-violet-6)' : '2px solid transparent',
                }}
                onClick={() => scrollToGroup(item.id)}
              >
                {item.label}
              </Text>
            ))}
          </Stack>
        </Box>
      </div>

      <MessageUsageModal
        opened={usageOpened}
        onClose={closeUsage}
        title={t('settings.quota.modal.title')}
        time={documentForm.values.question_date}
        robotPlanInfo={robotPlanInfo}
        centered
      />
    </>
  );
};
